import http from '@/utils/http';
import { history } from 'umi';
import { notification } from 'tea-component';

// taskId状态码
export enum FETCH_TASK_ID_STATUS_CODE {
  // 下载等待中
  DOWNLOAD_STATUS_WAIT = 0,
  // 下载中（后端读取数中）
  DOWNLOAD_STATUS_ING = 1,
  // 下载成功（后端读取数据完成，可以调用下载文件流接口了）
  DOWNLOAD_STATUS_OK = 2,
  // 下载取消（后端取消）
  DOWNLOAD_STATUS_CANCEL = 3,
}

// taskId状态码（下载正常进行中的taskId状态码）
export const NORMAL_FETCH_TASK_ID_STATUS_CODES = [
  FETCH_TASK_ID_STATUS_CODE.DOWNLOAD_STATUS_WAIT,
  FETCH_TASK_ID_STATUS_CODE.DOWNLOAD_STATUS_ING,
  FETCH_TASK_ID_STATUS_CODE.DOWNLOAD_STATUS_OK,
];

// 获取taskId错误码
enum FETCH_TASK_ID_ERR_CODE {
  // 队列中不存在taskId
  ERROR_DOWNLOAD_NOT_EXIST_TASK = 40001,
  // task队列已到达上限，暂时不能继续下载
  ERROR_DOWNLOAD_MORE_THAN_MAX = 40002,
  // 下载日期范围超过一年，不能下载
  ERROR_DOWNLOAD_DATE_TOO_MUCH = 40003,
  // 后端导出EXCEL文件时异常
  ERROR_DOWNLOAD_EXPORT_EXCEL = 40004,
  // 下载任务取消（后端原因判断取消）
  ERROR_DOWNLOAD_TASK_CANCEL = 40005,
  // 重复下载
  ERROR_DOWNLOAD_DUPLICATE = 40006,
  // 下载日期范围超过五年，不能下载
  ERROR_DOWNLOAD_DATE_TOO_MUCH_5YEAR = 40007,
}

export const FETCH_TASK_ID_ERR_MSG_CONFIG: any = {
  [FETCH_TASK_ID_ERR_CODE.ERROR_DOWNLOAD_NOT_EXIST_TASK]: {
    type: 'error',
    msg: '40001 - unknow exception occured when download.',
  },
  [FETCH_TASK_ID_ERR_CODE.ERROR_DOWNLOAD_MORE_THAN_MAX]: {
    type: 'error',
    msg: 'You are operating too frequently, please try again later.',
  },
  [FETCH_TASK_ID_ERR_CODE.ERROR_DOWNLOAD_DATE_TOO_MUCH]: {
    type: 'error',
    msg: 'Each download is only allowed within one year period, please adjust the selected time range.',
  },
  [FETCH_TASK_ID_ERR_CODE.ERROR_DOWNLOAD_EXPORT_EXCEL]: {
    type: 'error',
    msg: '40004 - unknow exception occured when download.',
  },
  [FETCH_TASK_ID_ERR_CODE.ERROR_DOWNLOAD_TASK_CANCEL]: {
    type: 'error',
    msg: '40005 - unknow exception occured when download.',
  },
  [FETCH_TASK_ID_ERR_CODE.ERROR_DOWNLOAD_DUPLICATE]: {
    type: 'warning',
    msg: 'The task is downloading, please wait until the download is complete.',
  },
  [FETCH_TASK_ID_ERR_CODE.ERROR_DOWNLOAD_DATE_TOO_MUCH_5YEAR]: {
    type: 'error',
    msg: 'Each download is only allowed within five year period, please adjust the selected time range.',
  },
};

export const notificationDownloadError = (exception: any) => {
  const fetchTaskErrMsgConfig = FETCH_TASK_ID_ERR_MSG_CONFIG[exception?.code];
  const type: 'success' | 'warning' | 'error' = fetchTaskErrMsgConfig?.type || 'error';
  const msg = fetchTaskErrMsgConfig?.msg || exception?.msg || exception?.message || 'fail';
  notification[type]({
    unique: true,
    // className: 'no-close-icon',
    description: msg,
  });
};

export function fetchDownloadTaskId(api: string, params: any, config?: any): Promise<any> {
  return http(api, {
    method: 'post',
    data: params,
    rawData: true,
    errorHandler: true,
    ...config,
  });
}

export async function getDownloadTaskId(
  api: string,
  params: any,
  config?: any,
): Promise<string> {
  try {
    const { data } = await fetchDownloadTaskId(api, params, config);
    let taskId;
    taskId = data?.task_id;
    if (!taskId)
      throw new Error(
        'an error occured when fetch download task id in getDownloadTaskId: task id is invalid.',
      );
    // console.log(taskId);
    return taskId;
  } catch (e: any) {
    notificationDownloadError(e);
    throw new Error(e);
  }
}

// 开启下载任务
export const startDownloadTask = (params: { task_id: number; }, config?: any): Promise<any> => downloadCustomConfig({
  url: `/api/v1/${baseSystematicName()}/startDownloadData`,
  method: 'post',
  data: params,
  rawData: true,
  ...config,
}) as Promise<any>;

/**
 * 根据任务id 下载文件
 * @param params 文件下载入参
 * @param params.taskId 下载任务id
 * @returns 二进制数据
 */
// 下载接口超时时间
export const DOWNLOAD_TIMEOUT = 2 * 60 * 60 * 1000;
export async function downloadFileByTaskId(params: { task_id: number; }, config?: any): Promise<any> {
  return downloadCustomConfig({
    url: `/api/v1/${baseSystematicName()}/getTaskDownloadData`,
    method: 'post',
    data: params,
    responseType: 'arraybuffer',
    original: true,
    timeout: DOWNLOAD_TIMEOUT,
    ...config,
  }) as Promise<any>;
}

export async function downloadFileCOSPathByTaskId(params: { task_id: number; }, config?: any): Promise<any> {
  return downloadCustomConfig({
    url: `/api/v1/${baseSystematicName()}/getTaskDownloadPath`,
    method: 'post',
    data: params,
    timeout: DOWNLOAD_TIMEOUT,
    ...config,
  }) as Promise<any>;
}

// 获取各系统路径Url
export const baseSystematicName = () => {
  const baseUrl = history.location.pathname.split('/')?.[1];
  const config: {
    [key in 'intelligence' | 'dashboard' | 'datalab' | string]: string;
  } = {
    intelligence: 'intelligence_pc',
    dashboard: 'dashboard',
    datalab: 'datalab',
  };
  return config?.[baseUrl] || 'intelligence_pc';
};

/**
 * 各个系统下载接口的 自定义配置
 * @param config
 */
const downloadCustomConfig = (config: any) => {
  const baseSystem = baseSystematicName();
  if (baseSystem === 'datalab') { // Datalab系统下载页面特殊处理
    const {
      token, // 下载页面需要单独鉴权, 而不是获取系统的token
      dashboardId, // 承接页面报表id
    } = history?.location?.query || {};
    if (token) { // 承接页面的报表 表格下载
      config.data = {...config.data, dashboard_id: dashboardId};
      config.headers = {...config.headers, Authorization: token};
    }
  }

  // 其他系统特殊处理

  return http(config);
};
